import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { lowStockAlertsAPI } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  AlertTriangle, Search, RefreshCw, Package, 
  Bell, BellOff, Check, XCircle, AlertOctagon,
  Store, Warehouse, Eye, EyeOff
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const LowStockAlerts = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('active');
  const [filterSeverity, setFilterSeverity] = useState('all');
  
  // Acknowledge dialog
  const [ackDialogOpen, setAckDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [ackNotes, setAckNotes] = useState('');
  const [savingAck, setSavingAck] = useState(false);

  const entityType = user?.entity_type;
  const entityId = user?.entity_id;

  const fetchAlerts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        status: filterStatus !== 'all' ? filterStatus : undefined,
        severity: filterSeverity !== 'all' ? filterSeverity : undefined,
        limit: 200
      };
      
      // Add location based on entity type
      if (entityType === 'store') {
        params.store_id = entityId;
      } else if (entityType === 'warehouse') {
        params.warehouse_id = entityId;
      }
      
      const response = await lowStockAlertsAPI.getAlerts(params);
      setAlerts(response.data.alerts || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast({ title: 'Error', description: 'Failed to fetch alerts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, filterStatus, filterSeverity, toast]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return (
          <Badge className="bg-red-100 text-red-700 gap-1">
            <AlertOctagon className="w-3 h-3" />
            Critical
          </Badge>
        );
      case 'warning':
        return (
          <Badge className="bg-amber-100 text-amber-700 gap-1">
            <AlertTriangle className="w-3 h-3" />
            Warning
          </Badge>
        );
      default:
        return (
          <Badge className="bg-yellow-100 text-yellow-700 gap-1">
            <Bell className="w-3 h-3" />
            Low
          </Badge>
        );
    }
  };

  const getLocationIcon = (locationType) => {
    if (locationType === 'store') {
      return <Store className="w-4 h-4 text-blue-600" />;
    }
    return <Warehouse className="w-4 h-4 text-purple-600" />;
  };

  const openAckDialog = (alert) => {
    setSelectedAlert(alert);
    setAckNotes('');
    setAckDialogOpen(true);
  };

  const handleAcknowledge = async () => {
    if (!selectedAlert) return;
    
    setSavingAck(true);
    try {
      await lowStockAlertsAPI.acknowledgeAlert(
        selectedAlert.product_id, 
        selectedAlert.location_id, 
        { notes: ackNotes || null }
      );
      
      toast({ title: 'Success', description: 'Alert acknowledged successfully' });
      setAckDialogOpen(false);
      fetchAlerts();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
      toast({ title: 'Error', description: 'Failed to acknowledge alert', variant: 'destructive' });
    } finally {
      setSavingAck(false);
    }
  };

  const handleRemoveAck = async (alert) => {
    try {
      await lowStockAlertsAPI.removeAcknowledgement(alert.product_id, alert.location_id);
      toast({ title: 'Success', description: 'Alert reactivated' });
      fetchAlerts();
    } catch (error) {
      console.error('Error removing acknowledgement:', error);
      toast({ title: 'Error', description: 'Failed to reactivate alert', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="low-stock-alerts-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-600" />
            Low Stock Alerts
          </h1>
          <p className="text-gray-500 mt-1">
            {entityType === 'hq' 
              ? 'View and manage low stock alerts across all locations'
              : 'View low stock alerts for your location'}
          </p>
        </div>
        <Button onClick={fetchAlerts} variant="outline" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertOctagon className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Critical</p>
                <p className="text-xl font-bold text-red-600">{summary.critical_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Warning</p>
                <p className="text-xl font-bold text-amber-600">{summary.warning_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Package className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Alerts</p>
                <p className="text-xl font-bold text-gray-900">{summary.total_alerts || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Acknowledged</p>
                <p className="text-xl font-bold text-emerald-600">{summary.acknowledged_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Bell className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Unacknowledged</p>
                <p className="text-xl font-bold text-purple-600">{summary.unacknowledged_count || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Alerts</SelectItem>
                  <SelectItem value="active">Active Only</SelectItem>
                  <SelectItem value="acknowledged">Acknowledged Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-[180px]">
              <Label className="text-gray-600 text-sm mb-1 block">Severity</Label>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger data-testid="severity-filter">
                  <SelectValue placeholder="Filter by severity" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Severities</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts Table */}
      <Card>
        <CardHeader className="border-b bg-gray-50/50 py-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-600" />
            Active Alerts
          </CardTitle>
          <CardDescription>
            Products that need attention due to low stock levels
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Product</TableHead>
                  {entityType === 'hq' && <TableHead>Location</TableHead>}
                  <TableHead className="text-right">Current Stock</TableHead>
                  <TableHead className="text-right">Threshold</TableHead>
                  <TableHead className="text-right">Shortage</TableHead>
                  <TableHead className="text-center">Severity</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={entityType === 'hq' ? 8 : 7} className="text-center py-8 text-gray-500">
                      Loading alerts...
                    </TableCell>
                  </TableRow>
                ) : alerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={entityType === 'hq' ? 8 : 7} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <Check className="w-12 h-12 text-emerald-500" />
                        <p className="text-gray-500">No low stock alerts!</p>
                        <p className="text-sm text-gray-400">All products are well stocked</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  alerts.map((alert, idx) => (
                    <TableRow 
                      key={`${alert.product_id}-${alert.location_id}`}
                      className={
                        alert.severity === 'critical' ? 'bg-red-50' : 
                        alert.severity === 'warning' ? 'bg-amber-50' : ''
                      }
                      data-testid={`alert-row-${idx}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-gray-900">{alert.product_name}</p>
                          <p className="text-sm text-gray-500">{alert.sku}</p>
                          {alert.category_name && (
                            <p className="text-xs text-gray-400">{alert.category_name}</p>
                          )}
                        </div>
                      </TableCell>
                      {entityType === 'hq' && (
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {getLocationIcon(alert.location_type)}
                            <div>
                              <p className="font-medium text-gray-900">{alert.location_name}</p>
                              <p className="text-xs text-gray-500">{alert.location_code}</p>
                            </div>
                          </div>
                        </TableCell>
                      )}
                      <TableCell className="text-right">
                        <span className={alert.current_stock <= 0 ? 'text-red-600 font-bold' : 'font-medium'}>
                          {alert.current_stock}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-gray-600">{alert.alert_threshold}</span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="text-red-600 font-medium">
                          -{alert.shortage_qty}
                        </span>
                      </TableCell>
                      <TableCell className="text-center">
                        {getSeverityBadge(alert.severity)}
                      </TableCell>
                      <TableCell className="text-center">
                        {alert.is_acknowledged ? (
                          <div className="flex flex-col items-center">
                            <Badge className="bg-emerald-100 text-emerald-700 gap-1">
                              <Check className="w-3 h-3" />
                              Acknowledged
                            </Badge>
                            <p className="text-xs text-gray-500 mt-1">{alert.acknowledged_by}</p>
                          </div>
                        ) : (
                          <Badge className="bg-purple-100 text-purple-700 gap-1">
                            <Bell className="w-3 h-3" />
                            Active
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {alert.is_acknowledged ? (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleRemoveAck(alert)}
                            title="Reactivate alert"
                            data-testid={`reactivate-btn-${idx}`}
                          >
                            <EyeOff className="w-3 h-3 mr-1" />
                            Reactivate
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openAckDialog(alert)}
                            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid={`acknowledge-btn-${idx}`}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Acknowledge
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Acknowledge Dialog */}
      <Dialog open={ackDialogOpen} onOpenChange={setAckDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Check className="w-5 h-5 text-emerald-600" />
              Acknowledge Alert
            </DialogTitle>
            <DialogDescription>
              Acknowledge this low stock alert to indicate you're aware of it
            </DialogDescription>
          </DialogHeader>
          
          {selectedAlert && (
            <div className="space-y-4 py-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900">{selectedAlert.product_name}</p>
                <p className="text-sm text-gray-500">SKU: {selectedAlert.sku}</p>
                <div className="mt-2 flex items-center gap-4 text-sm">
                  <span>
                    Current: <strong className="text-red-600">{selectedAlert.current_stock}</strong>
                  </span>
                  <span>
                    Threshold: <strong>{selectedAlert.alert_threshold}</strong>
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={ackNotes}
                  onChange={(e) => setAckNotes(e.target.value)}
                  placeholder="Add any notes about this alert (e.g., order placed, awaiting delivery)"
                  rows={3}
                  data-testid="ack-notes-input"
                />
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleAcknowledge} 
              disabled={savingAck}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-ack-btn"
            >
              {savingAck ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Acknowledge
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LowStockAlerts;
